<template>
  <v-card
    v-if="isSupported"
    class="rounded-lg fill-height"
  >
    <v-card-title class="pa-4 d-flex justify-space-between align-center">
      <div class="subtitle-2 grey--text text--grey-100">
        {{ chartTitle }}
      </div>
      <div class="d-flex align-center">
        <v-select
          v-if="tagOptions.length > 5"
          v-model="selectedTagId"
          :items="tagOptions"
          item-value="id"
          item-text="label"
          dense
          outlined
          hide-details
          style="max-width: 180px;"
          class="caption"
          @change="$emit('tag-change', $event)"
        />
        <v-btn-toggle
          v-else-if="tagOptions.length > 0"
          v-model="selectedTagId"
          mandatory
          dense
          color="primary"
        >
          <v-btn
            v-for="tag in tagOptions"
            :key="tag.id"
            :value="tag.id"
            x-small
            text
            class="text-none"
            @click="$emit('tag-change', tag.id)"
          >
            {{ tag.label }}
          </v-btn>
        </v-btn-toggle>
        <v-btn
          v-if="removable"
          icon
          style="pointer-events: auto"
          @click="$emit('remove')"
        >
          <v-icon small>
            fal fa-times
          </v-icon>
        </v-btn>
      </div>
    </v-card-title>
    <v-card-text>
      <apexchart
        v-if="series.length > 0"
        type="bar"
        :height="chartHeight"
        :options="chartOptions"
        :series="series"
      />
      <div
        v-else
        class="d-flex justify-center align-center"
        style="height: 200px"
      >
        <span class="grey--text">{{ $t('coach_insights.no_data') }}</span>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { getTranslatedTagValue } from '@/utils/helpers'

const SUPPORTED_KEYS = ['COACH_TAG_SUMMARY', 'PLAYER_TAG_SUMMARY']
const COLORS = ['#1976D2', '#388E3C', '#F57C00', '#D32F2F', '#7B1FA2', '#0097A7', '#5D4037', '#455A64', '#C2185B', '#00796B', '#FFA000', '#512DA8']

export default {
  name: 'IndividualTagSummaryChart',
  components: {
    apexchart: VueApexCharts
  },
  props: {
    chart: {
      type: Object,
      required: true
    },
    stats: {
      type: Array,
      required: true
    },
    variables: {
      type: Array,
      default: () => []
    },
    height: {
      type: [String, Number],
      default: 350
    },
    removable: {
      type: Boolean,
      default: false
    },
    initialTag: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      selectedTagId: this.initialTag || null
    }
  },
  computed: {
    isSupported() {
      return this.chart && SUPPORTED_KEYS.includes(this.chart.key)
    },
    chartVariable() {
      if (!this.chart || !this.chart.variable || !this.chart.category) return null
      return this.variables.find(v =>
        v.value === this.chart.variable && v.category === this.chart.category
      ) || null
    },
    chartTitle() {
      if (this.chartVariable) {
        return this.chartVariable.long_text || this.chartVariable.text || this.chart.variable
      }
      return this.$t('coach_insights.chart.title')
    },
    availableTags() {
      if (!this.chartVariable || !this.chartVariable.suffix_agg) return []
      return Object.keys(this.chartVariable.suffix_agg).map(tagId => ({
        id: tagId,
        label: this.formatTagId(tagId)
      }))
    },
    tagOptions() {
      return this.availableTags
    },
    statPrefix() {
      if (!this.chart || !this.chart.category || !this.chart.variable) return ''
      return `${this.chart.category.toLowerCase()}.${this.chart.variable}`
    },
    chartHeight() {
      return this.height
    },
    entityStats() {
      if (!this.stats || this.stats.length === 0) return null
      return this.stats[0]?.stats || null
    },
    tagData() {
      if (!this.entityStats || !this.selectedTagId || !this.statPrefix) return {}
      const tagStatsKey = `${this.statPrefix}_${this.selectedTagId}`
      return this.entityStats[tagStatsKey] || {}
    },
    sortedTagEntries() {
      return Object.entries(this.tagData)
        .map(([rawName, val]) => {
          let numericVal = 0
          if (typeof val === 'number') {
            numericVal = val
          } else if (val && typeof val === 'object' && typeof val.total === 'number') {
            numericVal = val.total
          }
          return {
            name: rawName,
            label: getTranslatedTagValue(rawName),
            value: Math.round(numericVal * 100) / 100
          }
        })
        .filter(e => e.value > 0)
        .sort((a, b) => b.value - a.value)
    },
    series() {
      if (this.sortedTagEntries.length === 0) return []
      return [{
        name: this.chartTitle,
        data: this.sortedTagEntries.map(e => e.value)
      }]
    },
    chartOptions() {
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '60%',
            distributed: true
          }
        },
        xaxis: {
          categories: this.sortedTagEntries.map(e => e.label),
          labels: {
            rotate: -45
          }
        },
        yaxis: {
          min: 0
        },
        colors: COLORS,
        fill: {
          opacity: 0.85
        },
        legend: {
          show: false
        },
        dataLabels: {
          enabled: false
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val
            }
          }
        }
      }
    }
  },
  watch: {
    availableTags: {
      immediate: true,
      handler(tags) {
        if (!this.selectedTagId && tags.length > 0) {
          this.selectedTagId = tags[0].id
        }
      }
    }
  },
  methods: {
    formatTagId(tagId) {
      if (tagId === 'group_id') return this.$t('insights.by_group')
      return tagId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    }
  }
}
</script>
